import {
  Card,
  Divider,
  Drawer,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Zoom
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme
} from '@material-ui/core/styles';
import {
  AddRounded,
  DeleteRounded,
  EditRounded,
  MoreVertRounded
} from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as RRLink, NavLink } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { ORDER } from '../../../common/enum/order';
import { PaginationProps } from '../../../common/interface/pagination';
import EnhancedTableToolbar from '../../../components/EnhancedTableToolbar';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import {
  AccessControl,
  getAccessControlsMasterDataAsync
} from './accessControlSlice';

interface AccessControlParamProps extends PaginationProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      borderRadius: '16px',
      display: 'flex',
      padding: theme.spacing(1, 1, 1, 2),
      marginBottom: theme.spacing(1.5),
      '&:last-child': {
        marginBottom: 0
      }
    },
    cardContainer: {
      flexGrow: 1,
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    width50: {
      width: '50%',
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      }
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5)
    },
    fab: {
      paddingRight: theme.spacing(3),
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 100
    },
    marginRight1: {
      marginRight: theme.spacing(1)
    }
  })
);

export default function AccessControlView() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const sortOptions = [
    { value: 'id', text: t('accessControlView.sortOptions.createdDate') },
    { value: 'name', text: t('accessControlView.sortOptions.name') }
  ];

  const { masterData: accessControls, count } = useSelector(
    (state: RootState) => state.accessControls
  );

  const [openOption, setOpenOption] = useState<boolean>(false);
  const [selectedAccessControl, setSelectedAccessControl] =
    useState<AccessControl | null>(null);
  const [accessControlParams, setAccessControlParams] =
    useState<AccessControlParamProps>({
      page: 0,
      limit: 30,
      sortBy: 'id',
      orderBy: ORDER.asc
    });

  useEffect(() => {
    loadingAccessControl(accessControlParams);
  }, []);

  function loadingAccessControl(params: AccessControlParamProps) {
    dispatch(getAccessControlsMasterDataAsync(params));
    setAccessControlParams(params);
  }

  function handleChangePage(
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) {
    loadingAccessControl({
      ...accessControlParams,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  function toggleDrawer(open: boolean, accessControl?: AccessControl) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && accessControl) {
        setSelectedAccessControl(accessControl);
      }
      setOpenOption(open);
    };
  }

  return (
    <div className={classes.root}>
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem>
              <ListItemText primary={selectedAccessControl?.name} />
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem
              component={NavLink}
              to={`access-controls/${selectedAccessControl?.id}/edit`}
              button
            >
              <ListItemIcon>
                <EditRounded />
              </ListItemIcon>
              <ListItemText primary={t('accessControlView.drawer.editAccessControl')} />
            </ListItem>
            <ListItem button onClick={() => {}} disabled>
              <ListItemIcon>
                <DeleteRounded />
              </ListItemIcon>
              <ListItemText
                primary={t('accessControlView.drawer.deleteAccessControl')}
                secondary={t('accessControlView.drawer.functionNotAvailable')}
              />
            </ListItem>
          </List>
        </div>
      </Drawer>
      <Breadcrumb
        name="masterDataName"
        data={{
          masterDataName: t('accessControlView.breadcrumb.accessControl'),
          masterDataRef: 'access-controls'
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('accessControlView.title')}
      </Typography>
      <EnhancedTableToolbar
        name={t('accessControlView.toolbar.name') as string}
        params={accessControlParams}
        sortOptions={sortOptions}
        loadingElement={loadingAccessControl}
      />
      {accessControls.map((accessControl) => {
        return (
          <Card className={classes.card} key={accessControl.id}>
            <div className={classes.cardContainer}>
              <div className={classes.width50}>
                <Typography>{accessControl.name}</Typography>
              </div>
              <div className={classes.width50}>
                <Typography>{accessControl.description}</Typography>
              </div>
            </div>
            <div>
              <IconButton
                size="small"
                onClick={toggleDrawer(true, accessControl)}
              >
                <MoreVertRounded />
              </IconButton>
            </div>
          </Card>
        );
      })}
      <div className={classes.pagination}>
        <Pagination
          count={Math.ceil(count / accessControlParams.limit)}
          shape="rounded"
          page={accessControlParams.page + 1}
          onChange={handleChangePage}
        />
      </div>
      <Zoom
        in
        timeout={transitionDuration}
        style={{
          transitionDelay: `${transitionDuration.exit}ms`
        }}
        unmountOnExit
      >
        <Fab
          className={classes.fab}
          component={RRLink}
          to={`access-controls/create`}
          variant="extended"
          color="primary"
          aria-label={t('accessControlView.fab.ariaLabel') as string}
        >
          <AddRounded className={classes.marginRight1} />
          {t('accessControlView.fab.newAccessControl')}
        </Fab>
      </Zoom>
    </div>
  );
}
